import React, { useRef, useState, useEffect } from 'react';

const CertificadoMedico = ({ patient, clinicalData, onCancel, onSaveSuccess }) => {
    const printRef = useRef(null);

    // Initial state setup based on patient and clinical data
    const [localData, setLocalData] = useState({
        nombres: `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim(),
        cedula: patient?.cedula || '',
        dob: patient?.birth_date ? patient.birth_date.split('-').reverse().join('/') : '',
        edad: '', // Will be calculated in useEffect
        domicilio: patient?.address || '',
        ocupacion: patient?.occupation || '',
        
        doctora: clinicalData?.professional || 'Dra. Silvana Ginnelle Estrada Castro',
        doctoraCedula: '0401908850', // Mockup default
        doctoraReg: clinicalData?.professional_code || '1019-2021-2292400',
        
        fechaEmision: new Date().toLocaleDateString('es-EC', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'America/Guayaquil' }),
        ciudad: 'Tulcán',
        
        enfermedadActual: clinicalData?.current_illness || '',
        diagnostico: (clinicalData?.diagnoses || []).filter(d => d && (d.cie || d.code) && d.description && d.description !== 'undefined').map(d => `${d.cie || d.code} ${d.description}`).join(', ') || '',
        
        recomendaciones: clinicalData?.consent_data?.certificate_data?.recomendaciones || '',
        fechaEvaluacion: clinicalData?.consent_data?.certificate_data?.fechaEvaluacion || (clinicalData?.control_date ? new Date(clinicalData.control_date).toLocaleDateString('es-EC') : new Date().toLocaleDateString('es-EC')),
        horaAtencion: clinicalData?.consent_data?.certificate_data?.horaAtencion || '1 hora'
    });

    const [saving, setSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null); // 'saved', 'error'

    useEffect(() => {
        if (clinicalData?.consent_data?.certificate_data) {
            const cert = clinicalData.consent_data.certificate_data;
            setLocalData(prev => ({
                ...prev,
                ...cert
            }));
        }
    }, [clinicalData]);

    useEffect(() => {
        if (patient?.birth_date && !localData.edad) {
            const birthDate = new Date(patient.birth_date);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            setLocalData(prev => ({ ...prev, edad: `${age} años` }));
        }
    }, [patient, localData.edad]);

    const handleChange = (field) => (e) => {
        setLocalData(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleSaveCertificate = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

            const recEl = document.getElementById('cert-recomendaciones');
            const actualRecomendaciones = recEl ? recEl.innerText : localData.recomendaciones;

            const certPayload = {
                ...localData,
                recomendaciones: actualRecomendaciones
            };

            let recordId = clinicalData?.id;

            if (recordId) {
                const currentConsent = typeof clinicalData.consent_data === 'object' && clinicalData.consent_data !== null ? clinicalData.consent_data : {};
                const updatedConsent = {
                    ...currentConsent,
                    certificate_data: certPayload
                };

                const response = await fetch(`${API_URL}/api/clinical-records/${recordId}/`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        consent_data: updatedConsent
                    })
                });

                if (response.ok) {
                    setSaveStatus('saved');
                    if (onSaveSuccess) onSaveSuccess();
                    setTimeout(() => {
                        if (onCancel) onCancel(true); // Pasar true para evitar confirm modal
                    }, 1200);
                } else {
                    setSaveStatus('error');
                }
            } else {
                const newRecord = {
                    patient: patient.id,
                    reason_for_consultation: 'Certificado de Salud Visual',
                    current_illness: localData.enfermedadActual || '',
                    consent_data: {
                        certificate_data: certPayload
                    }
                };

                const response = await fetch(`${API_URL}/api/clinical-records/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(newRecord)
                });

                if (response.ok) {
                    setSaveStatus('saved');
                    if (onSaveSuccess) onSaveSuccess();
                    setTimeout(() => {
                        if (onCancel) onCancel(true);
                    }, 1200);
                } else {
                    setSaveStatus('error');
                }
            }
        } catch (error) {
            console.error('Error al guardar certificado:', error);
            setSaveStatus('error');
        }
        setSaving(false);
    };

    const handlePrint = async () => {
        const html2pdf = (await import('html2pdf.js')).default;
        
        const input = document.getElementById("print-area");
        
        const opt = {
            margin: [5, 5, 5, 5],
            filename: `Certificado_${localData.nombres}.pdf`,
            image: { type: 'jpeg', quality: 1 },
            html2canvas: { scale: 2, useCORS: true, scrollY: 0, windowWidth: 1200 },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };
        
        try {
            await html2pdf().set(opt).from(input).save();
        } catch (error) {
            console.error("Error generando PDF", error);
        }
    };

    if (!patient) return null;

    // Styles
    const themeBlue = '#2c4f8c';
    const themeGreen = '#72a061';
    
    const pageStyle = {
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        backgroundColor: 'white',
        width: '210mm',
        minHeight: '297mm',
        margin: '0 auto',
        padding: '0',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'hidden' // for the curved background
    };

    const editableInputStyle = {
        border: 'none',
        outline: 'none',
        backgroundColor: 'transparent',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        color: 'inherit',
        fontWeight: 'inherit',
        padding: '2px 5px',
        borderBottom: '1px solid black',
        width: '100%'
    };

    // SVG Backgrounds
    const TopCurve = () => (
        <svg viewBox="0 0 800 150" style={{ position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 0 }}>
            <path d="M0,0 L800,0 L800,50 Q400,150 0,50 Z" fill="#e5e7eb" opacity="0.6" />
            <path d="M0,0 L800,0 L800,30 Q400,100 0,30 Z" fill="#f3f4f6" opacity="0.5" />
        </svg>
    );

    const BottomCurve = () => (
        <svg viewBox="0 0 800 150" style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', zIndex: 0 }}>
            <path d="M0,150 L800,150 L800,100 Q400,0 0,100 Z" fill="#e5e7eb" opacity="0.6" />
            <path d="M0,150 L800,150 L800,120 Q400,50 0,120 Z" fill="#f3f4f6" opacity="0.5" />
        </svg>
    );

    return (
        <div className="cert-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 10000, overflowY: 'auto' }}>
            
            {/* Control Bar (Not printed) */}
            <div className="d-print-none sticky-top" style={{ backgroundColor: '#111827', padding: '15px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
                <button onClick={handleSaveCertificate} disabled={saving} className="btn btn-outline-success fw-bold px-4 rounded-pill">
                    <i className={`bi ${saving ? 'bi-hourglass-split' : 'bi-save-fill'} me-2`}></i> 
                    {saving ? 'Guardando...' : 'Guardar Certificado'}
                </button>
                <button onClick={handlePrint} className="btn btn-light fw-bold px-4 rounded-pill">
                    <i className="bi bi-file-earmark-pdf-fill me-2"></i> Descargar PDF
                </button>
                <button onClick={onCancel} className="btn btn-outline-danger fw-bold px-4 rounded-pill">
                    <i className="bi bi-x-circle-fill me-2"></i> Cerrar
                </button>

                {saveStatus === 'saved' && (
                    <span className="badge bg-success py-2 px-3 fs-6 rounded-pill animate__animated animate__fadeIn">
                        <i className="bi bi-check-circle-fill me-1"></i> ¡Certificado guardado con éxito!
                    </span>
                )}
                {saveStatus === 'error' && (
                    <span className="badge bg-danger py-2 px-3 fs-6 rounded-pill animate__animated animate__fadeIn">
                        <i className="bi bi-exclamation-triangle-fill me-1"></i> Error al guardar
                    </span>
                )}
            </div>

            {/* Print Area */}
            <style>
                {`
                    @media print {
                        body * {
                            visibility: hidden;
                        }
                        .cert-overlay {
                            position: absolute !important;
                            left: 0 !important;
                            top: 0 !important;
                            width: 100% !important;
                            height: 100% !important;
                            background: none !important;
                            overflow: visible !important;
                        }
                        #print-area, #print-area * {
                            visibility: visible !important;
                        }
                        #print-area {
                            position: absolute !important;
                            left: 0 !important;
                            top: 0 !important;
                            width: 100% !important;
                            height: 100% !important;
                            min-height: 100% !important;
                            margin: 0 !important;
                            padding: 0 !important;
                            box-shadow: none !important;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        @page {
                            size: A4;
                            margin: 0;
                        }
                        input, textarea {
                            background-color: transparent !important;
                            border-bottom: none !important;
                        }
                    }
                `}
            </style>

            <div id="print-area" ref={printRef} style={{ ...pageStyle, margin: '20px auto', boxShadow: '0 0 20px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column' }}>
                
                <TopCurve />

                <div style={{ position: 'relative', zIndex: 1, padding: '25mm 20mm 20mm 20mm', display: 'flex', flexDirection: 'column', flex: 1, boxSizing: 'border-box' }}>
                    
                    {/* Header Logo */}
                    <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                        <img src="/vision-click.png" alt="Vision-Click" style={{ maxHeight: '80px', objectFit: 'contain' }} />
                    </div>

                    {/* Title */}
                    <div style={{ textAlign: 'center', marginBottom: '20px', borderBottom: `3px solid ${themeGreen}`, paddingBottom: '10px' }}>
                        <h1 style={{ color: themeBlue, fontSize: '32px', fontWeight: '900', margin: 0, letterSpacing: '1px' }}>CERTIFICADO DE SALUD VISUAL</h1>
                    </div>

                    {/* City and Date */}
                    <div style={{ textAlign: 'right', fontWeight: 'bold', marginBottom: '40px', fontSize: '14px' }}>
                        <span contentEditable suppressContentEditableWarning onBlur={e => setLocalData(prev => ({ ...prev, ciudad: e.target.innerText }))} style={{ outline: 'none' }}>{localData.ciudad}</span>{' '}
                        <span contentEditable suppressContentEditableWarning onBlur={e => setLocalData(prev => ({ ...prev, fechaEmision: e.target.innerText }))} style={{ outline: 'none' }}>{localData.fechaEmision}</span>
                    </div>

                    {/* Patient Info Block */}
                    <div style={{ paddingLeft: '50px', marginBottom: '30px', fontSize: '12px', fontWeight: 'bold', lineHeight: '1.8' }}>
                        <div style={{ display: 'flex' }}>
                            <span style={{ width: '150px' }}>NOMBRES Y APELLIDOS:</span>
                            <input type="text" value={localData.nombres} onChange={handleChange('nombres')} style={{...editableInputStyle, borderBottom: '1px solid black', flex: 1}} />
                        </div>
                        <div style={{ display: 'flex' }}>
                            <span style={{ width: '150px' }}>CÉDULA DE IDENTIDAD:</span>
                            <input type="text" value={localData.cedula} onChange={handleChange('cedula')} style={{...editableInputStyle, borderBottom: '1px solid black', flex: 1}} />
                        </div>
                        <div style={{ display: 'flex' }}>
                            <span style={{ width: '150px' }}>FECHA DE NACIMIENTO:</span>
                            <input type="text" value={localData.dob} onChange={handleChange('dob')} style={{...editableInputStyle, borderBottom: '1px solid black', flex: 1}} />
                        </div>
                        <div style={{ display: 'flex' }}>
                            <span style={{ width: '150px' }}>EDAD:</span>
                            <input type="text" value={localData.edad} onChange={handleChange('edad')} style={{...editableInputStyle, borderBottom: '1px solid black', flex: 1}} />
                        </div>
                        <div style={{ display: 'flex' }}>
                            <span style={{ width: '150px' }}>DOMICILIO:</span>
                            <input type="text" value={localData.domicilio} onChange={handleChange('domicilio')} style={{...editableInputStyle, borderBottom: '1px solid black', flex: 1}} />
                        </div>
                        <div style={{ display: 'flex' }}>
                            <span style={{ width: '150px' }}>OCUPACIÓN:</span>
                            <input type="text" value={localData.ocupacion} onChange={handleChange('ocupacion')} style={{...editableInputStyle, borderBottom: '1px solid black', flex: 1}} />
                        </div>
                    </div>

                    {/* Body Text */}
                    <div style={{ fontSize: '14px', lineHeight: '1.6', textAlign: 'justify', marginBottom: '20px' }}>
                        <p style={{ marginBottom: '20px' }}>
                            <strong>Yo, <span contentEditable suppressContentEditableWarning onBlur={e => setLocalData(prev => ({ ...prev, doctora: e.target.innerText }))} style={{ outline: 'none', fontWeight: 'bold' }}>{localData.doctora}</span></strong>, 
                            certifico que el paciente <span contentEditable suppressContentEditableWarning onBlur={e => setLocalData(prev => ({ ...prev, nombres: e.target.innerText }))} style={{ outline: 'none', color: '#6c757d' }}>{localData.nombres.toUpperCase()}</span>, 
                            portador/a de la cédula de identidad N.º <span contentEditable suppressContentEditableWarning onBlur={e => setLocalData(prev => ({ ...prev, cedula: e.target.innerText }))} style={{ outline: 'none', color: '#6c757d' }}>{localData.cedula}</span>.
                        </p>

                        <p style={{ marginBottom: '20px' }}>
                            <strong>Asistió a la consulta de salud visual / optométrica presentando</strong> "<span contentEditable suppressContentEditableWarning onBlur={e => setLocalData(prev => ({ ...prev, enfermedadActual: e.target.innerText }))} style={{ outline: 'none', color: '#6c757d' }}>{localData.enfermedadActual || 'Enfermedad o problema actual'}</span>". 
                            <strong> Con el diagnóstico de</strong> "<span contentEditable suppressContentEditableWarning onBlur={e => setLocalData(prev => ({ ...prev, diagnostico: e.target.innerText }))} style={{ outline: 'none', color: '#6c757d' }}>{localData.diagnostico || 'Código CIE 10 y descripción'}</span>"
                        </p>

                        <div style={{ marginBottom: '20px' }}>
                            <strong>Recomendaciones / Prescripción Óptica:</strong> 
                            <div id="cert-recomendaciones" contentEditable suppressContentEditableWarning onBlur={e => setLocalData(prev => ({ ...prev, recomendaciones: e.target.innerText }))} style={{ outline: 'none', color: '#6c757d', marginTop: '5px', minHeight: '60px', width: '100%', border: '1px dotted #ccc', padding: '5px' }}>
                                {localData.recomendaciones}
                            </div>
                        </div>

                        <p style={{ marginBottom: '20px', color: '#6c757d' }} contentEditable suppressContentEditableWarning style={{ outline: 'none', color: '#6c757d' }}>
                            Se emite el presente certificado, para los fines que el paciente considere convenientes.
                        </p>

                        <div style={{ color: '#6c757d' }}>
                            <p style={{ margin: 0 }}><strong>Fecha de evaluación:</strong> <span contentEditable suppressContentEditableWarning onBlur={e => setLocalData(prev => ({ ...prev, fechaEvaluacion: e.target.innerText }))} style={{ outline: 'none', color: '#6c757d' }}>{localData.fechaEvaluacion}</span></p>
                            <p style={{ margin: 0 }}><strong>Hora de atención:</strong> <span contentEditable suppressContentEditableWarning onBlur={e => setLocalData(prev => ({ ...prev, horaAtencion: e.target.innerText }))} style={{ outline: 'none', color: '#6c757d' }}>{localData.horaAtencion}</span></p>
                        </div>
                    </div>

                    {/* Footer Content */}
                    <div style={{ marginTop: 'auto', paddingTop: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', zIndex: 1 }}>
                        
                        {/* Signature block */}
                        <div style={{ fontSize: '12px', fontWeight: 'bold', lineHeight: '1.5' }}>
                            <span contentEditable suppressContentEditableWarning onBlur={e => setLocalData(prev => ({ ...prev, doctora: e.target.innerText }))} style={{ outline: 'none', fontWeight: 'bold', display: 'inline-block', paddingTop: '5px', minWidth: '200px', textAlign: 'center' }}>
                                {localData.doctora}
                            </span><br/>
                            OPTOMETRISTA / OFTALMÓLOGA<br/>
                            N° Reg. <span contentEditable suppressContentEditableWarning onBlur={e => setLocalData(prev => ({ ...prev, doctoraReg: e.target.innerText }))} style={{ outline: 'none' }}>{localData.doctoraReg}</span><br/>
                            C.I <span contentEditable suppressContentEditableWarning onBlur={e => setLocalData(prev => ({ ...prev, doctoraCedula: e.target.innerText }))} style={{ outline: 'none' }}>{localData.doctoraCedula}</span>
                        </div>

                        {/* Social & QR */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 'bold', lineHeight: '1.5' }}>
                                <div><i className="bi bi-whatsapp"></i> 0962602999</div>
                                <div><i className="bi bi-instagram"></i> @visionclick.ec</div>
                                <div><i className="bi bi-geo-alt"></i> Entre Arellano<br/>&nbsp;&nbsp;&nbsp;&nbsp;y Ayacucho</div>
                            </div>
                            <img src="https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=https://wa.me/593962602999" alt="QR Code" style={{ width: '80px', height: '80px' }} />
                        </div>
                    </div>

                </div>

                <BottomCurve />
            </div>
        </div>
    );
};

export default CertificadoMedico;
